import React from 'react';
import { Paper, Typography, CircularProgress, useTheme, Box } from '@material-ui/core';
import { AccountStatementEntry } from '~/types';
import { usePicktowHistory } from '~/services/hooks/usePicktowHistory';
import Table from '~/components/Table';
import { formatPrice } from '~/util/format';

interface PicktowHistoryCardProps {
    associationId: string;
    associationName?: string;
}

export function PicktowHistoryCard({ associationId, associationName }: PicktowHistoryCardProps) {
    const theme = useTheme();
    const { accountStatement, loading, error } = usePicktowHistory(associationId);

    const formatDateTime = (timestamp: number | string) => {
        const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Paper style={{ marginTop: 30, overflow: 'hidden' }}>
            <Box
                style={{
                    padding: 20,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.secondary.main,
                }}
            >
                <Typography variant='h5' style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    Histórico{associationName ? ` - ${associationName}` : ''}
                </Typography>
            </Box>

            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' padding={5}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box padding={3} textAlign='center'>
                    <Typography color='textSecondary' variant='body1'>
                        Não há histórico disponível para esta empresa.
                    </Typography>
                    <Typography color='textSecondary' variant='body2' style={{ marginTop: 10 }}>
                        O histórico pode estar indisponível ou não foram realizadas transações ainda.
                    </Typography>
                </Box>
            ) : (
                <>
                    {accountStatement?.entries && accountStatement.entries.length > 0 ? (
                        <Table
                            columns={{
                                created_at: 'Data',
                                description: 'Descrição',
                                type: 'Tipo',
                                amount: 'Valor',
                                balance: 'Saldo',
                            }}
                            data={
                                accountStatement?.entries?.map((entry: AccountStatementEntry) => ({
                                    ...entry,
                                    created_at: formatDateTime(entry.created_at),
                                    type: entry.type === 'CREDIT' ? 'Crédito' : 'Débito',
                                    amount: formatPrice(entry.amount),
                                    balance: entry.balance !== undefined ? formatPrice(entry.balance) : '-',
                                })) || []
                            }
                            localization={{
                                body: {
                                    emptyDataSourceMessage: 'Sem histórico disponível',
                                },
                                pagination: {
                                    labelRowsSelect: 'registros',
                                },
                                header: {
                                    actions: '',
                                },
                            }}
                            options={{
                                showTitle: false,
                                search: false,
                                paging: true,
                                pageSize: 10,
                                pageSizeOptions: [10, 20, 50],
                            }}
                        />
                    ) : null}

                    <Box
                        style={{
                            padding: 20,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        {accountStatement?.available_balance !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px', fontWeight: 'bold', marginRight: 20 }}>
                                Saldo Disponível: {formatPrice(accountStatement.available_balance)}
                            </Typography>
                        )}
                        {accountStatement?.current_balance !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px', marginRight: 20 }}>
                                Saldo Atual: {formatPrice(accountStatement.current_balance)}
                            </Typography>
                        )}
                        {accountStatement?.previous_balance !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px', marginRight: 20 }}>
                                Saldo Anterior: {formatPrice(accountStatement.previous_balance)}
                            </Typography>
                        )}
                        {accountStatement?.provisional_balance !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px', marginRight: 20 }}>
                                Saldo Provisório: {formatPrice(accountStatement.provisional_balance)}
                            </Typography>
                        )}
                        {accountStatement?.reconciled_balance !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px', marginRight: 20 }}>
                                Saldo Conciliado: {formatPrice(accountStatement.reconciled_balance)}
                            </Typography>
                        )}
                        {accountStatement?.credit_limit !== undefined && (
                            <Typography variant='body1' style={{ fontSize: '14px' }}>
                                Limite de Crédito: {formatPrice(accountStatement.credit_limit)}
                            </Typography>
                        )}
                    </Box>
                </>
            )}
        </Paper>
    );
}

export default PicktowHistoryCard;
